import { Button, Box, Dialog, Typography } from "flux";
import { BaseDialogProps } from "@adora/shared/contexts/dialog-context";
import { Experiment } from "../../hooks/use-experiments";
import { useExperimentInfo } from "../../hooks/use-experiment-info";

interface ViewExperimentDetailsDialogProps extends BaseDialogProps {
  experiment: Experiment;
}

const ViewExperimentDetailsDialog: React.FC<ViewExperimentDetailsDialogProps> = ({ experiment, open, onClose }) => {
  const { info, isLoading, error } = useExperimentInfo(experiment.brand_id, experiment.id);

  const renderInfoSection = (title: string, info: Record<string, unknown> | null) => {
    if (!info) return null;

    return (
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        {Object.entries(info).map(([key, value]) => (
          <Box key={key} mb={1}>
            <Typography variant="subtitle2">{key}</Typography>
            <Typography variant="body2">
              {typeof value === "object" ? JSON.stringify(value, null, 2) : `${value}`}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={() => onClose(true)} maxWidth="md" fullWidth dataDogIdentifier="experiment details">
      <Box p={3}>
        <Typography variant="h4" gutterBottom>
          Experiment Details
        </Typography>

        {/* Basic experiment info */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <Typography>Loading details...</Typography>
          </Box>
        ) : (
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Box mb={1}>
              <Typography variant="subtitle2">ID</Typography>
              <Typography variant="body2">{experiment.id}</Typography>
            </Box>
            <Box mb={1}>
              <Typography variant="subtitle2">Name</Typography>
              <Typography variant="body2">{experiment.name}</Typography>
            </Box>
            <Box mb={1}>
              <Typography variant="subtitle2">Description</Typography>
              <Typography variant="body2">{experiment.description}</Typography>
            </Box>
            {experiment.product && (
              <Box mb={1}>
                <Typography variant="subtitle2">Product</Typography>
                <Typography variant="body2">{experiment.product}</Typography>
              </Box>
            )}
            {experiment.filtered_hit_rate !== undefined && (
              <Box mb={1}>
                <Typography variant="subtitle2">Filtered Hit Rate</Typography>
                <Typography variant="body2">{`${(experiment.filtered_hit_rate * 100).toFixed(1)}%`}</Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Error state */}
        {error && (
          <Box mb={3}>
            <Typography color="error">Failed to load experiment details</Typography>
          </Box>
        )}

        {/* Fetched experiment info */}
        {!error && info && (
          <>
            {renderInfoSection("Caption Batch Information", info.caption_batch_info)}
            {renderInfoSection("Training Batch Information", info.training_batch_info)}
            {renderInfoSection("Product LoRA Model Information", info.product_lora_model_info)}
          </>
        )}

        {/* Action buttons */}
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button variant="contained" onClick={() => onClose(true)} dataDogIdentifier="experiment details dialog close">
            Close
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ViewExperimentDetailsDialog;
